// @ts-nocheck
/**
 * Product Decoration Pricing - Dynamic pricing display based on decoration type and quantity
 * Updates the strike-through current price and "as low as" best price
 *
 * Supports two container modes:
 * 1. [data-decoration-price-display] — custom-caps-individual only, rendered
 *    outside <product-price> so the WSAIO wholesale app cannot override it.
 * 2. [data-current-price] — legacy fallback inside <product-price> for any
 *    other template that enables decoration pricing.
 */
(function() {
  function initDecorationPricing() {
    // ---- Determine which container mode to use ----
    const initialContainer = document.querySelector('[data-decoration-price-display]')
      || document.querySelector('[data-current-price]');

    if (!initialContainer) {
      return;
    }

    const decorationPricingData = initialContainer.dataset.decorationPricing;

    if (!decorationPricingData || decorationPricingData === 'null' || decorationPricingData === '') {
      return;
    }

    let decorationPricing;
    try {
      decorationPricing = JSON.parse(decorationPricingData);
    } catch (e) {
      console.error('Decoration pricing: Failed to parse decoration pricing data:', e);
      return;
    }

    // Re-resolve live nodes on every update. Section re-renders (theme editor,
    // section-rendering API) replace these elements, so captured references
    // would point at detached DOM.
    function getRootContainer() {
      return document.querySelector('[data-decoration-price-display]')
        || document.querySelector('[data-current-price]');
    }

    function useNewContainer() {
      return Boolean(getRootContainer()?.hasAttribute('data-decoration-price-display'));
    }

    function getProductForm() {
      // The price block is a sibling of the product-form block, not nested inside it.
      // Find the related form within the same product-details container.
      return getRootContainer()?.closest('.product-details')?.querySelector('product-form-component')
        || document.querySelector('product-form-component');
    }

    function formContains(target) {
      const form = getProductForm();
      return Boolean(form && target instanceof Node && form.contains(target));
    }

    if (!getProductForm()) {
      return;
    }

    // ---- Legacy mode: watch for product-price replacing [ref="priceContainer"] ----
    let priceObserver;
    function bindLegacyPriceObserver() {
      if (useNewContainer()) return;
      const priceWrapper = getRootContainer()?.parentElement;
      if (!priceWrapper) return;
      priceObserver?.disconnect();
      priceObserver = new MutationObserver((mutations) => {
        const priceContainerReplaced = mutations.some((mutation) =>
          Array.from(mutation.removedNodes).some(
            (node) => node.nodeType === Node.ELEMENT_NODE && node.getAttribute?.('ref') === 'priceContainer'
          )
        );
        if (priceContainerReplaced) {
          updatePrices();
        }
      });
      priceObserver.observe(priceWrapper, { childList: true });
    }
    bindLegacyPriceObserver();

    function getDecorationKey(rawValue) {
      if (!rawValue) return '';
      const key = rawValue.replace(/\s*\(.*\)\s*$/, '').trim();
      const isNewProduction = getProductForm()?.dataset.productHandle?.includes('-new-production');
      const aliases = isNewProduction
        ? {
            'Puff Embroidery': 'Puff Embroidery (Add-on)',
            'TPU': 'TPU (Add-on)',
            'Silicone': 'Silicone (Add-on)',
            'Woven Patch': 'Woven Patch (Add-on)',
            'Woven Patch - Sew-on or Heat Press': 'Woven Patch (Add-on)',
            'Embroidery Patch': 'Embroidery Patch (Add-on)',
            'Embroidery Patch - Sew-on or Heat Press': 'Embroidery Patch (Add-on)',
          }
        : {
            'Woven Patch-Heat Press': 'Woven Patch',
            'Embroidery Patch-Heat Press': 'Embroidery Patch',
          };
      return aliases[key] || key;
    }

    function hasDecorationPrice(decorationType) {
      return Boolean(decorationPricing?.[decorationType])
        || (getProductForm()?.dataset.productHandle?.includes('-new-production') && decorationType === 'Imitation Leather Patch');
    }

    function getPriceElements() {
      const rootContainer = getRootContainer();
      if (useNewContainer()) {
        // New container mode — elements are direct children of [data-decoration-price-display]
        const currentPriceEl = rootContainer?.querySelector('[data-decoration-price-strikethrough]');
        const asLowAsPriceEl = rootContainer?.querySelector('[data-decoration-price-value]');
        return { currentPriceEl, asLowAsPriceEl };
      }
      // Legacy mode — elements inside product-price-container
      const currentPriceEl = rootContainer?.querySelector('[data-current-price-value]');
      const priceWrapper = rootContainer?.parentElement;
      const asLowAsPriceEl = priceWrapper?.querySelector('[ref="priceContainer"] .price');
      return { currentPriceEl, asLowAsPriceEl };
    }

    function findDecorationSelect() {
      const productForm = getProductForm();
      if (!productForm) return;
      const selects = Array.from(productForm.querySelectorAll('select[data-option-index]'));
      return selects.find(select =>
        Array.from(select.options).some(option => decorationPricing && decorationPricing[getDecorationKey(option.value)])
      );
    }

    // Decoration may render as a <select> or as radios inside a dropdown.
    function getSelectedDecorationValue() {
      const productForm = getProductForm();
      if (!productForm) return '';
      const select = findDecorationSelect();
      if (select) return select.value || '';
      const radio = Array.from(productForm.querySelectorAll('input[type="radio"][data-option-index]:checked'))
        .find(input => hasDecorationPrice(getDecorationKey(input.value))
          || input.value.replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase() === 'blank cap');
      return radio?.value || '';
    }

    // Mirror each decoration dropdown's checked option into its summary,
    // including injected price markup.
    function syncDropdownSummaries() {
      document.querySelectorAll('[data-decoration-dropdown]').forEach((dropdown) => {
        const valueEl = dropdown.querySelector('[data-decoration-dropdown-value]');
        const checked = dropdown.querySelector('input[type="radio"]:checked');
        const span = checked?.closest('label')?.querySelector('span');
        if (valueEl && span) valueEl.innerHTML = span.innerHTML;
      });
    }

    function updateDecorationOptionPrices(quantity) {
      const productForm = getProductForm();
      if (!productForm) return;

      const setupPerUnit = quantity > 0 ? 125 / quantity : 0;

      // Setup fee segment: "$X.XX {label}" when showing the price, or just the
      // merchant-editable label (e.g. "Setup Fees") when text-only.
      const pickerEl = productForm.querySelector('[data-dynamic-variant-picker]');
      const setupDisplay = pickerEl?.dataset.setupDisplay || 'price';
      const setupText = pickerEl?.dataset.setupText || 'Setup Fees';
      const setupLabel = setupDisplay === 'text' ? setupText : `$${setupPerUnit.toFixed(2)} ${setupText}`;

      const priceTextFor = (key) => {
        const price = getDecorationPrice(key, quantity, decorationPricing);
        const parts = [];
        if (price > 0 && key !== 'Flat Embroidery') parts.push(`$${price.toFixed(2)}`);
        if (setupPerUnit > 0) parts.push(setupLabel);
        return parts.length ? ` - ${parts.join(' + ')}` : '';
      };

      const priceSuffix = /\s*[-\u2013\u2014]\s*\$[\d.,]+.*$/;

      productForm.querySelectorAll('select[data-option-index]').forEach(select => {
        Array.from(select.options).forEach(option => {
          const key = getDecorationKey(option.value);
          if (!hasDecorationPrice(key)) return;

          const baseText = option.textContent.replace(priceSuffix, '').trim();
          option.textContent = `${baseText}${priceTextFor(key)}`;
        });
      });

      productForm.querySelectorAll('input[type="radio"][data-option-index]').forEach(input => {
        const key = getDecorationKey(input.value);
        if (!hasDecorationPrice(key)) return;

        const label = input.closest('label');
        const span = label?.querySelector('span');
        if (!span) return;

        // Cache the clean label once so injected pill markup never
        // contaminates the base text on re-render.
        if (!span.dataset.baseText) {
          span.dataset.baseText = span.textContent.replace(priceSuffix, '').trim();
        }

        const price = getDecorationPrice(key, quantity, decorationPricing);

        const priceStyle = input.closest('[data-decoration-dropdown]')?.dataset.priceStyle || 'pill';

        // Build the inline row: truncated label, then price + setup either as
        // pills or as a plain text suffix (" - $X.XX + $Y.YY setup").
        // textContent keeps merchant-supplied label text safe from HTML parsing.
        const labelSpan = document.createElement('span');
        labelSpan.className = 'decoration-option-label';
        labelSpan.textContent = span.dataset.baseText;

        span.replaceChildren(labelSpan);
        if (priceStyle === 'text') {
          const parts = [];
          if (price > 0 && key !== 'Flat Embroidery') parts.push(`$${price.toFixed(2)}`);
          if (setupPerUnit > 0) parts.push(setupLabel);
          if (parts.length) {
            const suffixSpan = document.createElement('span');
            suffixSpan.className = 'decoration-price-text';
            suffixSpan.textContent = `- ${parts.join(' + ')}`;
            span.appendChild(suffixSpan);
          }
        } else {
          if (price > 0 && key !== 'Flat Embroidery') {
            span.insertAdjacentHTML('beforeend', `<span class="decoration-price-pill">+$${price.toFixed(2)}</span>`);
          }
          if (setupPerUnit > 0) {
            const setupPill = document.createElement('span');
            setupPill.className = 'decoration-setup-pill';
            setupPill.textContent = setupDisplay === 'text' ? setupText : `+$${setupPerUnit.toFixed(2)} ${setupText}`;
            span.appendChild(setupPill);
          }
        }
      });

      syncDropdownSummaries();
    }

    function getSelectedAddonPrice() {
      const productForm = getProductForm();
      if (!productForm) return 0;
      const selects = productForm.querySelectorAll('select[data-addon-type="decoration"]');
      let total = 0;
      for (const select of selects) {
        if (!select.value) continue;
        const option = select.options[select.selectedIndex];
        if (!option) continue;
        total += Number(option.dataset.addonPrice || 0) / 100;
      }
      return total;
    }

    function isNfcSelected() {
      const productForm = getProductForm();
      if (!productForm) return false;
      const nfcInput = productForm.querySelector('[data-nfc-input][value="with-nfc"]:checked');
      if (nfcInput) return true;

      const nfcOption = productForm.querySelector('input[type="radio"][data-option-index][value*="NFC"]:checked');
      if (nfcOption) {
        const value = nfcOption.value.toLowerCase();
        if (value.includes('without')) return false;
        return /^with\b/.test(value);
      }

      return false;
    }

    function updatePrices(quantityValue) {
      const productForm = getProductForm();
      const { currentPriceEl, asLowAsPriceEl } = getPriceElements();
      if (!productForm || !currentPriceEl || !asLowAsPriceEl) {
        return;
      }

      // Get current quantity
      const quantityInput = productForm.querySelector('input[name="quantity"]');
      const quantity = Number(quantityValue) || parseInt(quantityInput?.value) || 48;

      // Get selected decoration type (select or radio, matched against decoration pricing keys)
      let decorationType = getDecorationKey(getSelectedDecorationValue());

      // Get base cap price and resolved shipping from the live product-delivery component.
      // The decoration-pricing table also has [data-cap-price], so we specifically target
      // the delivery root to avoid stale values and missing shipping.
      const deliveryRoot = productForm.querySelector('[data-product-delivery]');
      let capPrice = 0;

      if (deliveryRoot) {
        capPrice = parseFloat(deliveryRoot.getAttribute('data-cap-price')) || 0;
      } else if (!useNewContainer()) {
        // Fallback: try to get price from product price display (legacy mode only)
        const priceWrapper = getRootContainer()?.parentElement;
        const priceDisplay = priceWrapper?.querySelector('[ref="priceContainer"] .price');
        if (priceDisplay?.textContent) {
          const priceText = priceDisplay.textContent.replace(/[^0-9.]/g, '');
          capPrice = parseFloat(priceText) * 100 || 0;
        }
      }

      const isNewProduction = productForm.dataset.productHandle?.includes('-new-production');
      const isBlankCap = !decorationType || decorationType.toLowerCase() === 'blank cap';
      const flatKey = getDecorationKey('Flat Embroidery');
      const addonPrice = isBlankCap ? 0 : getSelectedAddonPrice();

      let baseDecorationPrice = 0;
      let currentDecorationPrice = 0;

      if (isNewProduction) {
        // New production: the variant price includes the base cap + Flat Embroidery.
        // Flat is always included; the selected picker decoration and any dropdown add-ons
        // are layered on top so each can have its own quantity-break pricing.
        // The variant price for new production already includes the base cap + Flat Embroidery.
        // Only subtract the Flat base, because optional add-ons are priced on top.
        baseDecorationPrice = getBaseDecorationPrice(flatKey, decorationPricing);

        const flatTiered = getDecorationPrice(flatKey, quantity, decorationPricing);
        const mainAddOnTiered = decorationType && decorationType !== flatKey
          ? getDecorationPrice(decorationType, quantity, decorationPricing)
          : 0;
        currentDecorationPrice = flatTiered + mainAddOnTiered + addonPrice;
      } else {
        // Stock/standard products: the variant price includes the selected decoration.
        baseDecorationPrice = getBaseDecorationPrice(decorationType, decorationPricing);
        currentDecorationPrice = getDecorationPrice(decorationType, quantity, decorationPricing) + addonPrice;
      }

      // data-cap-price already reflects the selected variant (with or without NFC)

      const capOnlyPrice = (capPrice / 100) - baseDecorationPrice;
      const setupPricePerUnit = isBlankCap ? 0 : 125 / quantity;
      const shippingPricePerUnit = Number(deliveryRoot?.getAttribute('data-shipping-unit-price') || 0);
      const currentTotalPrice = capOnlyPrice + (isBlankCap ? 0 : currentDecorationPrice) + setupPricePerUnit + shippingPricePerUnit;

      // The variant price stored in data-cap-price is the base price (cap + base decoration)
      // before any quantity-break discount. Use it as the strikethrough reference and show
      // the current quantity-break total in the main "as low as" price.
      const basePrice = capPrice / 100;

      currentPriceEl.textContent = formatCurrency(basePrice);

      if (currentTotalPrice < basePrice) {
        currentPriceEl.style.display = '';
      } else {
        currentPriceEl.style.display = 'none';
      }

      // Update "as low as" price (highlighted) — this is the price at the current quantity
      asLowAsPriceEl.textContent = formatCurrency(currentTotalPrice);
      asLowAsPriceEl.classList.add('price--highlighted');

      // Show the decoration price inside each decoration option label
      updateDecorationOptionPrices(quantity);
    }

    function getDecorationPrice(decorationType, quantity, pricingData) {
      if (!decorationType || !pricingData || !pricingData[decorationType]) {
        return 0;
      }

      const tiers = pricingData[decorationType];
      const tier = tiers.find(t => quantity >= t.min_qty && (t.max_qty == null || quantity <= t.max_qty));
      
      if (!tier) {
        return 0;
      }

      return tier.unit_price;
    }

    function getBestDecorationPrice(decorationType, pricingData) {
      if (!decorationType || !pricingData || !pricingData[decorationType]) {
        return 0;
      }

      const tiers = pricingData[decorationType];
      if (!tiers || !tiers.length) return 0;
      return Math.min(...tiers.map(t => t.unit_price));
    }

    function getBaseDecorationPrice(decorationType, pricingData) {
      if (!decorationType || !pricingData || !pricingData[decorationType]) {
        return 0;
      }

      const tiers = pricingData[decorationType];
      if (!tiers || !tiers.length) return 0;

      const baseTier = tiers.reduce((min, t) => (t.min_qty < min.min_qty ? t : min), tiers[0]);
      return baseTier.unit_price;
    }

    function formatCurrency(value) {
      return '$' + value.toFixed(2);
    }

    // Listeners delegate to document so they survive section re-renders
    // (theme editor, section-rendering API), which replace the productForm
    // element and would orphan bindings attached to it directly.
    document.addEventListener('delivery:price-update', function() {
      updatePrices();
    });

    document.addEventListener('quantity-selector:update', function(e) {
      if (!formContains(e.target)) return;
      const quantity = e.detail?.quantity;
      updatePrices(typeof quantity === 'number' ? quantity : undefined);
    });

    document.addEventListener('change', function(e) {
      if (!(e.target instanceof Element) || !formContains(e.target)) return;
      if (e.target.matches('input[name="quantity"]')) {
        updatePrices(e.target.value);
      } else if (e.target.matches('select[data-option-index], input[type="radio"][data-option-index], [data-nfc-input], input[data-addon-title], select[data-product-addon]')) {
        updatePrices();
      }
    });

    // Legacy mode only: direct quantity typing fallback.
    document.addEventListener('input', function(e) {
      if (useNewContainer()) return;
      if (!(e.target instanceof Element) || !formContains(e.target)) return;
      if (e.target.matches('input[name="quantity"]')) updatePrices(e.target.value);
    });

    document.addEventListener('variant:update', function() {
      updatePrices();
    });

    // Keep decoration dropdown summaries synced and closable even after a
    // section re-render, when the inline per-dropdown script's listeners die.
    document.addEventListener('change', function(e) {
      if (!(e.target instanceof HTMLInputElement) || e.target.type !== 'radio') return;
      const dropdown = e.target.closest('[data-decoration-dropdown]');
      if (!dropdown) return;
      syncDropdownSummaries();
      if (dropdown.open) {
        dropdown.open = false;
        dropdown.querySelector('summary')?.focus();
      }
    });

    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape' || !(e.target instanceof Element)) return;
      const dropdown = e.target.closest('[data-decoration-dropdown][open]');
      if (dropdown) {
        dropdown.open = false;
        dropdown.querySelector('summary')?.focus();
      }
    });

    // Theme editor / section-rendering API re-renders replace the nodes this
    // script captured, so re-bind the legacy observer and re-inject prices
    // into the fresh DOM.
    document.addEventListener('shopify:section:load', function() {
      bindLegacyPriceObserver();
      updatePrices();
    });

    // Initial update
    updatePrices();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDecorationPricing);
  } else {
    initDecorationPricing();
  }
})();
